import { CheckCircleOutlined } from '@ant-design/icons';
import { Button, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ThankYouConfig, WorkflowStep, WorkflowTemplate } from '@/api/workflows';

const { Title, Paragraph } = Typography;

/** BMO blue — the same primary the nav row's Next uses. */
const BMO_BLUE = '#0179C1';
const BODY_COLOR = '#334155';

/**
 * Product name for the confirmation headline: "your RDSP Application Package …".
 *
 * Authored on the ThankYou step (originator → Workflow details → the step's gear), with
 * the workflow's own title for the active language as the default. Each language resolves
 * independently — an authored English name is never shown inside French copy — and an
 * empty French value falls back on its own, which is what the modal saves when a workflow
 * has no French title to prefill from.
 *
 * This replaced an acronym heuristic (first all-caps token that wasn't "BMO"). It could
 * not tell a product acronym from an editorial annotation, so
 * `BMO Non-Registered / Registered Smart Form [DO NOT EDIT]` resolved to "DO" and its
 * French counterpart to "NE". Affix stripping was not a usable repair — the EN and FR
 * naming patterns do not correspond, and six of the ten seeded workflows carry no acronym
 * at all — so the guess gave way to an authored value plus a never-wrong default.
 */
export function productLabel(
  thankYouConfig: ThankYouConfig | null | undefined,
  template: Pick<WorkflowTemplate, 'nameEn' | 'nameFr'>,
  language: 'en' | 'fr',
): string {
  const authored =
    language === 'fr' ? thankYouConfig?.productTitleFr : thankYouConfig?.productTitleEn;
  if (authored?.trim()) return authored.trim();
  // Default. Mirrors the pre-existing localized-name pick: French falls through to the
  // English title when the workflow was never given a French one.
  const fallback = language === 'fr' && template.nameFr ? template.nameFr : template.nameEn;
  return (fallback ?? '').trim();
}

/**
 * Terminal confirmation card (2026-09-04). The advisor's flow ends here: FormShell renders
 * no navigation on this step, so the only ways out are the two CTAs below.
 *
 * Deliberately dumb — it reports what already happened. There is no submit call behind it:
 * the ladder has no SUBMITTED rung and GAMX will own submission (see api/applications.ts).
 * Reaching this step is the advisor being told the package is away, not us telling BMO.
 */
export function ThankYouStep({
  template,
  step,
  language,
  onBackToDashboard,
  variant = 'advisor',
}: {
  template: WorkflowTemplate;
  /** The ThankYou step itself — carries the authored product name. Optional so the
   *  existing call sites that only had a template keep working, and so a runtime reading
   *  an older published version (no config on the row) falls back rather than throwing. */
  step?: Pick<WorkflowStep, 'thankYouConfig'>;
  /** Advisor's UI locale — picks which language the product name resolves in. */
  language: 'en' | 'fr';
  /** Withheld in preview — an originator previewing a form has no advisor dashboard to
   *  return to, so the button is hidden rather than rendered onto a dead route. */
  onBackToDashboard?: () => void;
  /**
   * Whose ending this is, which decides the copy.
   *
   * The advisor variant says the package is on its way for e-signature — BMO holds the
   * application now. The public variant says the opposite: nothing was submitted anywhere,
   * the documents the customer just downloaded are the entire deliverable, and they need to
   * bring the printed forms to a branch. Telling a customer their application was submitted
   * would be false, and saying nothing leaves them holding a folder of PDFs with no idea
   * what to do next.
   */
  variant?: 'advisor' | 'public';
}) {
  const { t } = useTranslation();
  // Only the headline and body differ by audience — the close button, its refusal notice and
  // the product label read the same either way, so they stay on the shared keys.
  const copyPrefix =
    variant === 'public' ? 'formRenderer.thankYou.public' : 'formRenderer.thankYou';
  // Set when the browser refuses window.close(). See the handler.
  const [closeRefused, setCloseRefused] = useState(false);

  /**
   * AC5 asks for the tab to close. A page can only close a window that script opened —
   * so this works when the portal launched the smart form in a new tab, and is a silent
   * no-op when the advisor navigated here in the same one. A button that sometimes does
   * nothing at all reads as broken, so the refusal is surfaced as copy instead.
   *
   * `window.close()` does not throw when refused; it simply returns with the window still
   * open. Hence the post-hoc `closed` check rather than a try/catch.
   */
  const handleClose = () => {
    window.close();
    if (window.closed) return;
    // Refused (the tab was not opened by script). An advisor still gets out of the application:
    // Close falls back to their dashboard, because a Close that leaves the page exactly as it was
    // reads as broken (QA ESU-018). A public customer has no dashboard, so they get the notice.
    if (onBackToDashboard) onBackToDashboard();
    else setCloseRefused(true);
  };

  return (
    // Box styling (including the padding a phone drops) lives in global.css —
    // an inline `padding` here would outrank the container query that removes it.
    <div className="bmo-thank-you">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <CheckCircleOutlined
          style={{ fontSize: 28, color: BMO_BLUE, lineHeight: 1, marginTop: 2 }}
          aria-hidden
        />
        <Title level={3} style={{ margin: 0, fontSize: 22, lineHeight: 1.35 }}>
          {t(`${copyPrefix}.headline`, {
            product: productLabel(step?.thankYouConfig, template, language),
          })}
        </Title>
      </div>

      <Paragraph style={{ margin: '20px 0 0', color: BODY_COLOR, fontSize: 15 }}>
        {t(`${copyPrefix}.body`)}
      </Paragraph>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 28 }}>
        {onBackToDashboard && (
          <Button type="primary" size="large" shape="round" onClick={onBackToDashboard}>
            {t('formRenderer.thankYou.backToDashboard')}
          </Button>
        )}
        <Button size="large" shape="round" onClick={handleClose}>
          {t('formRenderer.thankYou.close')}
        </Button>
      </div>

      {closeRefused && (
        // role=status so a screen reader announces it — the visual change is the only
        // feedback a sighted user gets that the click did anything.
        <Paragraph role="status" style={{ margin: '12px 0 0', color: BODY_COLOR }}>
          {t('formRenderer.thankYou.closeBlocked')}
        </Paragraph>
      )}
    </div>
  );
}
